import {SedestralMemory} from "./SedestralMemory";
import {isLocalStorageAvailable} from "../utilities/IsLocalStorageAvailable";
import {AccountEvent} from "../../../models/account/event/AccountEvent";

export class SedestralStorage {
    private static isLocalStorageEnabled = true;

    public static async init(): Promise<void> {
        if (!isLocalStorageAvailable()) {
            this.isLocalStorageEnabled = false;
        }
    }

    public static setItem(key: string, value: string): void {
        if (this.isLocalStorageEnabled) {
            localStorage.setItem(key, value);
        } else {
            SedestralMemory.setItem(key, value);
        }
    }

    public static getItem(key: string): string | null {
        if (this.isLocalStorageEnabled) {
            return localStorage.getItem(key);
        } else {
            return SedestralMemory.getItem(key);
        }
    }

    public static hasItem(key: string): boolean {
        return this.getItem(key) != null;
    }

    public static removeItem(key: string): void {
        if (this.isLocalStorageEnabled) {
            localStorage.removeItem(key);
        } else {
            SedestralMemory.removeItem(key);
        }
    }

    /**
     * Mémoire propre à l'onglet : deux onglets ouverts côte à côte ne se marchent pas dessus.
     */
    public static setSessionItem(key: string, value: string): void {
        try {
            sessionStorage.setItem(key, value);
        } catch (e) {
            SedestralMemory.setItem(key, value);
        }
    }

    public static getSessionItem(key: string): string | null {
        try {
            return sessionStorage.getItem(key);
        } catch (e) {
            return SedestralMemory.getItem(key);
        }
    }

    public static removeSessionItem(key: string): void {
        try {
            sessionStorage.removeItem(key);
        } catch (e) {
            SedestralMemory.removeItem(key);
        }
    }

    public static setTempItem(key: string, value: string, ms: number) {
        if (this.hasItem(key)) {
            const raw = this.getItem(key);
            try {
                const temp = JSON.parse(raw);
                const updated = {...temp, value};
                this.setItem(key, JSON.stringify(updated));
                return;
            } catch (e) {

            }
        }

        const temp = {date: Date.now(), value, ms};
        this.setItem(key, JSON.stringify(temp));
    }

    public static getTempItem(key: string, orValue?: any): any {
        if (this.hasItem(key)) {
            const raw = this.getItem(key);
            try {
                const temp = JSON.parse(raw);
                const now = Date.now();

                if ((now - temp.date) < temp.ms) {
                    return temp.value;
                } else {
                    this.removeItem(key);
                }
            } catch (e) {
                this.removeItem(key);
            }
        }

        return orValue;
    }

    public static utmEvent(type: AccountEvent, data?: any) {
        this.setItem(`utmEvent`, JSON.stringify({
            type: type,
            data: data
        }));
    }

    public static setCookie(name: string, value: string, domain: string = location.hostname): void {
        try {
            let expireDate = new Date();
            expireDate.setFullYear(expireDate.getFullYear() + 1); // Expire après le nombre d'années spécifié
            document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expireDate.toUTCString()}; domain=${domain}; path=/`;
        } catch (e) {
            console.error("Failed to set cookie", e);
        }
    }

    public static getCookie(name: string): string | null {
        const nameEQ = encodeURIComponent(name) + "=";
        const cookies = document.cookie.split(';');

        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i];

            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1, cookie.length);
            }

            if (cookie.indexOf(nameEQ) === 0) {
                return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
            }
        }

        return null;
    }

    public static deleteCookie(name: string): void {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/';
    }
}